const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://masjid-app-funeral-production.up.railway.app/api'
  : (process.env.REACT_APP_API_URL || '/api');

class ApiService {
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async getMembers(filters = {}) {
    const queryParams = new URLSearchParams();
    if (filters.status && filters.status !== 'all') {
      queryParams.append('status', filters.status);
    }
    if (filters.search) {
      queryParams.append('search', filters.search);
    }
    const queryString = queryParams.toString();
    return this.request(`/members${queryString ? `?${queryString}` : ''}`);
  }

  async getMemberById(id) {
    return this.request(`/members/${id}`);
  }

  async createMember(memberData) {
    return this.request('/members', { method: 'POST', body: JSON.stringify(memberData) });
  }

  async updateMemberStatus(id, status) {
    return this.request(`/members/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
  }

  async updateMember(id, memberData) {
    return this.request(`/members/${id}`, { method: 'PUT', body: JSON.stringify(memberData) });
  }

  async getMembersDropdown() {
    return this.request('/members/dropdown');
  }

  async searchAll(q) {
    return this.request(`/members/search?q=${encodeURIComponent(q)}`);
  }

  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  async getDashboardTrends() {
    return this.request('/dashboard/trends');
  }

  async getDashboardAttention() {
    return this.request('/dashboard/attention');
  }

  async getDashboardFinancialOverview() {
    return this.request('/dashboard/financial-overview');
  }

  async getDashboardAnalytics(year) {
    const query = year ? `?year=${year}` : '';
    return this.request(`/dashboard/analytics${query}`);
  }

  async getDataQuality() {
    return this.request('/dashboard/data-quality');
  }

  async getSubscriptions() {
    return this.request('/subscriptions');
  }

  async createSubscription(subscriptionData) {
    return this.request('/subscriptions', { method: 'POST', body: JSON.stringify(subscriptionData) });
  }

  async updateSubscriptionStatus(id, status) {
    return this.request(`/subscriptions/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
  }

  async updateSubscription(id, subscriptionData) {
    return this.request(`/subscriptions/${id}`, { method: 'PUT', body: JSON.stringify(subscriptionData) });
  }

  async getPayments() {
    return this.request('/payments');
  }

  async getPaymentSummary() {
    return this.request('/payments/summary');
  }

  async getOverduePayments() {
    return this.request('/payments/overdue');
  }

  async getUnpaidPayments() {
    return this.request('/payments/unpaid');
  }

  async createContribution(data) {
    return this.request('/payments', { method: 'POST', body: JSON.stringify(data) });
  }

  async updatePayment(id, data) {
    return this.request(`/payments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async voidPayment(id, reason) {
    return this.request(`/payments/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async confirmPayment(id) {
    return this.request(`/payments/${id}/confirm`, { method: 'POST', body: JSON.stringify({}) });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword })
    });
  }

  async sendOverdueReminders() {
    return this.request('/notifications/send-overdue', { method: 'POST' });
  }

  async getNotificationStatus() {
    return this.request('/notifications/status');
  }

  async getDeceasedRecords() {
    return this.request('/deceased/enhanced');
  }

  async getDeceasedDetails(id) {
    return this.request(`/deceased/${id}/details`);
  }

  async getFamilyMembers(memberId) {
    return this.request(`/members/${memberId}/family`);
  }

  async recordDeceasedWithTransfer(deceasedData) {
    return this.request('/deceased/with-transfer', { method: 'POST', body: JSON.stringify(deceasedData) });
  }

  async recordDeceased(deceasedData) {
    return this.request('/deceased', { method: 'POST', body: JSON.stringify(deceasedData) });
  }

  async getSubscriptionPayments(memberId) {
    return this.request(`/subscriptions/${memberId}/payments`);
  }

  async markPayment(subscriptionId, paymentData) {
    return this.request(`/subscriptions/${subscriptionId}/mark-payment`, { method: 'POST', body: JSON.stringify(paymentData) });
  }

  async getMemberSummary(id) {
    return this.request(`/members/${id}/summary`);
  }

  async getMemberTimeline(memberId) {
    return this.request(`/members/${memberId}/timeline`);
  }

  async deactivateMember(id) {
    return this.request(`/members/${id}/deactivate`, { method: 'PUT' });
  }

  async rejectPromotion(id, reason) {
    return this.request(`/members/${id}/reject-promotion`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async getContributionBreakdown(memberId) {
    return this.request(`/members/${memberId}/contribution-breakdown`);
  }

  async recalculateContribution(memberId) {
    return this.request(`/members/${memberId}/recalculate-contribution`, { method: 'POST' });
  }

  async healthCheck() {
    return this.request('/health');
  }

  async addFamilyMember(memberId, data) {
    return this.request(`/members/${memberId}/family`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateFamilyMember(memberId, fmId, data) {
    return this.request(`/members/${memberId}/family/${fmId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteFamilyMember(memberId, fmId) {
    return this.request(`/members/${memberId}/family/${fmId}`, { method: 'DELETE' });
  }

  // Contributions grid: member × month for a given year
  async getPaymentsGrid(year) {
    return this.request(`/payments/grid?year=${year}`);
  }

  // Member notes
  async getAllNotes() {
    return this.request('/notes');
  }

  async getMemberNotes(memberId) {
    return this.request(`/members/${memberId}/notes`);
  }

  async createMemberNote(memberId, note) {
    return this.request(`/members/${memberId}/notes`, { method: 'POST', body: JSON.stringify(note) });
  }

  async updateMemberNote(memberId, noteId, note) {
    return this.request(`/members/${memberId}/notes/${noteId}`, { method: 'PUT', body: JSON.stringify(note) });
  }

  async deleteMemberNote(memberId, noteId) {
    return this.request(`/members/${memberId}/notes/${noteId}`, { method: 'DELETE' });
  }

  async saveLegacyPayments(memberId, data) {
    return this.request(`/members/${memberId}/legacy-payments`, {
      method: 'PUT',
      body: JSON.stringify({ data }),
    });
  }

  async reportIssue({ title, body, category }) {
    return this.request('/issues', {
      method: 'POST',
      body: JSON.stringify({ title, body, category }),
    });
  }

  async getReportedIssues(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status)   params.append('status', filters.status);
    if (filters.category) params.append('category', filters.category);
    const qs = params.toString();
    return this.request(`/issues${qs ? `?${qs}` : ''}`);
  }

  async updateIssueStatus(id, status) {
    return this.request(`/issues/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async getUsers() {
    return this.request('/auth/users');
  }

  async createUser(data) {
    return this.request('/auth/users', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateUserRole(id, role) {
    return this.request(`/auth/users/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) });
  }

  async updateUserEmail(id, email) {
    return this.request(`/auth/users/${id}/email`, { method: 'PATCH', body: JSON.stringify({ email }) });
  }

  async updateUserStatus(id, isActive) {
    return this.request(`/auth/users/${id}/status`, { method: 'PATCH', body: JSON.stringify({ isActive }) });
  }
}

const apiService = new ApiService();
export default apiService;
export { ApiService };
